import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Tooltip,
  Icon,
  Button,
  Text,
  Input,
  VStack,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tfoot,
  Select,
  IconButton,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useColorMode,
  Divider,
  HStack,
  Spacer,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
} from "@chakra-ui/react";
import axiosInstance from "../../helpers/axiosInstance";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import usePersistedState from "../usePersistedState";
import SettingsMenu from "../common/SettingsMenu";

function RoleManagement() {
  const [users, setUsers] = useState([]);
  const roles = ["Admin", "User", "Creator", "Business"];
  const [emailToDelete, setEmailToDelete] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { colorMode } = useColorMode();
  const handleOpen = () => {
    setIsOpen(true);
  };

  const [errors, setErrors] = useState({}); // For form validation errors
  // Validate form data
  const validate = (data) => {
    const errors = {};
    if (!data.username) errors.username = "Username is required";
    if (!data.email) errors.email = "Email is required";
    if (!data.password) errors.password = "Password is required";
    if (!data.role) errors.role = "Role is required";
    return errors;
  };
  const initialState = {
    username: "",
    email: "",
    password: "",
    role: "",
  };

  const [newUserData, setNewUserData] = useState(initialState);
  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = usePersistedState("perPage", 5); // Default to 10 messages per page
  const [totalPages, setTotalPages] = useState(0);

  const [isRoleChangeAlertOpen, setIsRoleChangeAlertOpen] = useState(false);
  const [isDeleteUserAlertOpen, setDeleteUserAlertOpen] = useState(false);
  const [userToChange, setUserToChange] = useState({ email: "", role: "" });
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usernameSearchInput, setUsernameSearchInput] = useState("");
  const [emailSearchInput, setEmailSearchInput] = useState("");

  const fetchUsers = () => {
    const requestData = {
      page: currentPage, // Add page number to the request
      per_page: perPage, // Number of items per page
    };

    axiosInstance
      .post(`/auth/admin/get_users`, requestData)
      .then((response) => {
        // console.log("API Response:", response.data.users_info);
        if (response.data && response.data.users_info.users) {
          const fetchedUsers = response.data.users_info.users;
          setUsers(fetchedUsers);
          // Initialize filtered users; subsequent effects will refine based on filters
          setFilteredUsers(fetchedUsers);
          if (response.data && response.data.users_info) {
            setTotalPages(response.data.users_info.total_pages);
            if (currentPage > totalPages) {
              setCurrentPage(Math.max(totalPages, 1));
            }
          }
        } else {
          console.error("Unexpected API response format");

          toast({
            title: "Error",
            description: "An Error Occurred.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
      });
  };

  // Handle role filter changes
  const handleRoleFilterChange = (selectedRoleValues) => {
    setSelectedRoles(selectedRoleValues);
  };

  useEffect(() => {
    // Only fetch messages if perPage is a valid number greater than 0
    if (perPage > 0) {
      fetchUsers();
    }
  }, [currentPage, perPage]); // Include currentPage here

  // Apply combined filters whenever users or any filter input changes
  useEffect(() => {
    let updated = Array.isArray(users) ? [...users] : [];

    if (usernameSearchInput) {
      const query = usernameSearchInput.toLowerCase();
      updated = updated.filter(
        (u) => u.username && u.username.toLowerCase().includes(query)
      );
    }

    if (emailSearchInput) {
      const query = emailSearchInput.toLowerCase();
      updated = updated.filter(
        (u) => u.email && u.email.toLowerCase().includes(query)
      );
    }

    if (selectedRoles.length > 0) {
      updated = updated.filter((u) => selectedRoles.includes(u.role));
    }

    setFilteredUsers(updated);
  }, [users, usernameSearchInput, emailSearchInput, selectedRoles]);

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow the field to be empty
    setPerPage(value === "" ? "" : Math.max(1, parseInt(value, 10)));
  };

  const handleInputBlur = () => {
    // When input loses focus, enforce minimum and correct invalid entries
    setPerPage((prev) =>
      prev === "" || isNaN(prev) ? 1 : Math.max(1, parseInt(prev, 10))
    );
  };

  function getDeviceDetails() {
    const ua = navigator.userAgent;

    // Detecting the browser
    let browser;
    if (/chrome|chromium|crios/i.test(ua)) {
      browser = "Chrome";
    } else if (/firefox|fxios/i.test(ua)) {
      browser = "Firefox";
    } else if (/safari/i.test(ua)) {
      browser = "Safari";
    } else if (/edg/i.test(ua)) {
      browser = "Edge";
    } else if (/opr\//i.test(ua)) {
      browser = "Opera";
    } else if (/msie|trident/i.test(ua)) {
      browser = "IE";
    } else {
      browser = "Unknown Browser";
    }

    // Detecting the platform
    let os;
    if (/android/i.test(ua)) {
      os = "Android";
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      os = "iOS";
    } else if (/win/i.test(ua)) {
      os = "Windows";
    } else if (/mac/i.test(ua)) {
      os = "MacOS";
    } else if (/linux/i.test(ua)) {
      os = "Linux";
    } else {
      os = "Unknown OS";
    }

    return {
      full_user_agent: navigator.userAgent,
      simplified_user_agent: `${os} ${browser}`,
      registration_date: new Date().toISOString().slice(0, 10),
    };
  }

  const addUser = () => {
    //console.log("newUserData", newUserData);
    const deviceDetails = getDeviceDetails();

    const signinPayload = {
      ...newUserData,
      device: deviceDetails,
    };

    axiosInstance
      .post("/auth/signup", signinPayload)
      .then((response) => {
        //console.log("newUserData response", response);
        if (response.status === 200) {
          toast({
            title: "Status",
            description: "User Created Successesfully",
            status: "success",
            duration: 9000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: "An Error Occurred.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }

        setNewUserData(initialState); // Reset the state
        setIsOpen(false); // Close the modal
        fetchUsers(); // Refresh the user list after adding
      })
      .catch((error) => {
        //console.log(error);
        if (error.response && error.response.status === 400) {
          toast({
            title: "User Creation Failed",
            description: "User already exists.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      });
  };

  const changeUserRole = (email, role) => {
    //console.log("Changing user role function");
    const payload = {
      email: email,
      role: role,
    };
    //console.log("payload:", payload);
    axiosInstance
      .put("/auth/admin/change_role", payload)
      .then((response) => {
        if (response.status === 200) {
          toast({
            title: "Status",
            description: "Role changed successfully",
            status: "success",
            duration: 9000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: "An Error Occurred.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        // Refresh the user list
        fetchUsers();
      })
      .catch((error) => {
        //console.log(error);
      });
  };

  const deleteUser = () => {
    axiosInstance
      .delete(
        `/auth/admin/delete_user?email=${encodeURIComponent(emailToDelete)}`
      )
      .then((response) => {
        // Refresh the user list
        fetchUsers();
        if (response.status === 200) {
          toast({
            title: "Status",
            description: "User Deleted Successesfully",
            status: "success",
            duration: 9000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        //console.log(error);
      });
  };

  // Handle form submission
  const handleSubmit = () => {
    // Check if the password is at least 8 characters.
    if (newUserData.password.length < 8) {
      setPasswordError("Password should be at least 8 characters.");

      // Show the error toast
      toast({
        title: "Error",
        description: "Password should be at least 8 characters.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return; // Stop further execution
    }
    const validationErrors = validate(newUserData);
    if (Object.keys(validationErrors).length === 0) {
      // No errors, you can now submit data
      //console.log(newUserData);
      addUser();
    } else {
      setErrors(validationErrors);
    }
  };

  return (
    <Box h="100vh">
      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        maxH="8vh"
        borderBottom="1px"
        borderColor="blackAlpha.200"
        overflowY="hidden"
      >
        <Box
          display="flex"
          alignItems="center"
          bg={colorMode === "dark" ? "gray.700" : "white"}
          width="100%"
          height="100%"
          p={4}
          pt={2}
          pb={2}
          boxSizing="border-box"
          overflowY="hidden"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mb={0}
            // mt={2}
            color={colorMode === "dark" ? "gray.200" : "blackAlpha.700"}
          >
            Role Management
          </Text>
          <Spacer />
          <Button
            mr={2}
            bg="#286328"
            _hover="none"
            textColor="white"
            borderRadius="xl"
            size="sm"
            leftIcon={<AddIcon />}
            onClick={() => setShowAddUserModal(true)}
          >
            Add User
          </Button>
          <SettingsMenu />
        </Box>
      </Box>
      <Box w="100%">
        {/* <Divider m="0" /> */}
        <Box h="77.5%" overflowX="auto" scroll="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>
                  <HStack spacing={2} justify="left">
                    <Text fontSize="sm" fontWeight="bold" mb={0}>USERNAME</Text>
                    <Menu closeOnSelect={false}>
                      <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                      <MenuList maxH="300px" overflowY="auto">
                        <Box p={2}>
                          <Input
                            size="sm"
                            placeholder="Type to search..."
                            value={usernameSearchInput}
                            onChange={(e) => setUsernameSearchInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={2} justify="left">
                    <Text fontSize="sm" fontWeight="bold" mb={0}>EMAIL</Text>
                    <Menu closeOnSelect={false}>
                      <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                      <MenuList maxH="300px" overflowY="auto">
                        <Box p={2}>
                          <Input
                            size="sm"
                            placeholder="Type to search..."
                            value={emailSearchInput}
                            onChange={(e) => setEmailSearchInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={2} justify="left">
                    <Text fontSize="sm" fontWeight="bold" mb={0}>ROLE</Text>
                    <Menu closeOnSelect={false}>
                      <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                      <MenuList>
                        <MenuGroup title="Select Roles">
                          <MenuOptionGroup
                            type="checkbox"
                            value={selectedRoles}
                            onChange={handleRoleFilterChange}
                          >
                            {roles.map((role) => (
                              <MenuItemOption key={role} value={role}>
                                {role}
                              </MenuItemOption>
                            ))}
                          </MenuOptionGroup>
                        </MenuGroup>
                        <MenuDivider />
                        <MenuItem onClick={() => setSelectedRoles(roles)}>
                          Select All Roles
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Th>
                <Th>ACTIONS</Th>
                <Th>CHANGE ROLE</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Array.isArray(filteredUsers) &&
                filteredUsers.slice(0, perPage).map((user, index) => (
                  <Tr key={index}>
                    <Td textAlign="left">{user.username}</Td>
                    <Td textAlign="left">{user.email}</Td>
                    <Td textAlign="left">{user.role}</Td>
                    <Td>
                      <Button
                        colorScheme="red"
                        bgColor="red.500"
                        textColor="white"
                        size="sm"
                        onClick={() => {
                          setEmailToDelete(user.email);
                          setDeleteUserAlertOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </Td>
                    <AlertDialog
                      isOpen={isDeleteUserAlertOpen}
                      onClose={() => setDeleteUserAlertOpen(false)}
                    >
                      <AlertDialogOverlay>
                        <AlertDialogContent>
                          <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete User
                          </AlertDialogHeader>

                          <AlertDialogBody>
                            Are you sure you want to delete the user?
                          </AlertDialogBody>

                          <AlertDialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setDeleteUserAlertOpen(false)}
                            >
                              No
                            </Button>
                            <Button
                              colorScheme="red"
                              bgColor="red.500"
                              textColor="white"
                              onClick={() => {
                                deleteUser();
                                setDeleteUserAlertOpen(false);
                              }}
                              ml={3}
                            >
                              Yes
                            </Button>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialogOverlay>
                    </AlertDialog>
                    <Td textAlign="left">
                      <Select
                        value={user.role}
                        onChange={(e) => {
                          const updatedRole = e.target.value;
                          setUserToChange({
                            email: user.email,
                            role: updatedRole,
                          });
                          setIsRoleChangeAlertOpen(true);
                        }}
                      >
                        <option value="" disabled>
                          Select a role
                        </option>
                        {roles.map((role, idx) => (
                          <option key={idx} value={role}>
                            {role}
                          </option>
                        ))}
                      </Select>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
            <Tfoot ml={0}></Tfoot>
          </Table>
        </Box>
        <Box mt={2} mb={2} ml={4} display="flex" justifyContent="left">
          <HStack spacing={4} mt={2} mb={2} align="center">
            <Button
              size="sm"
              onClick={handlePreviousClick}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <Text pt={3} size="sm">
              {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              onClick={handleNextClick}
              disabled={currentPage >= totalPages}
            >
              Next
            </Button>
            <Box>
              <FormControl>
                <HStack>
                  <FormLabel htmlFor="per-page" pt={2} size="sm">
                    Per Page:
                  </FormLabel>
                  <Input
                    size="sm"
                    id="per-page"
                    type="number"
                    value={perPage}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    min={1}
                    max={100}
                    width="100px"
                  />
                </HStack>
              </FormControl>
            </Box>
          </HStack>
        </Box>

        <AlertDialog
          isOpen={isRoleChangeAlertOpen}
          onClose={() => setIsRoleChangeAlertOpen(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Change Role
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to change the role?
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsRoleChangeAlertOpen(false)}
                >
                  No
                </Button>
                <Button
                  colorScheme="red"
                  bgColor="red.500"
                  textColor="white"
                  onClick={() => {
                    changeUserRole(userToChange.email, userToChange.role);
                    setIsRoleChangeAlertOpen(false);
                  }}
                  ml={3}
                >
                  Yes
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
      <Modal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={errors.username}>
              <FormLabel htmlFor="text">Username</FormLabel>
              <Input
                id="text"
                type="text"
                placeholder="Your username"
                maxLength={25}
                value={newUserData.username}
                onChange={(e) =>
                  setNewUserData({
                    ...newUserData,
                    username: e.target.value,
                  })
                }
              />
              <FormErrorMessage>{errors.username}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.email} mt={4}>
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                id="email"
                type="email"
                placeholder="Your email"
                maxLength={80}
                value={newUserData.email}
                onChange={(e) =>
                  setNewUserData({ ...newUserData, email: e.target.value })
                }
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!passwordError} mt={4}>
              <FormLabel htmlFor="password">Password</FormLabel>
              <Input
                id="password"
                type="password"
                placeholder="Your password"
                value={newUserData.password}
                onChange={(e) => {
                  const passwordValue = e.target.value;
                  if (passwordValue.length < 8) {
                    setPasswordError(
                      "Password should be at least 8 characters."
                    );
                    setIsPasswordValid(false);
                  } else {
                    setPasswordError("");
                    setIsPasswordValid(true);
                  }
                  setNewUserData({
                    ...newUserData,
                    password: passwordValue,
                  });
                }}
              />
              <FormErrorMessage>{passwordError}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.role} mt={4}>
              <FormLabel>Role</FormLabel>
              <Select
                value={newUserData.role}
                onChange={(e) =>
                  setNewUserData({ ...newUserData, role: e.target.value })
                }
              >
                <option value="" disabled>
                  Select a role
                </option>
                {roles.map((role, idx) => (
                  <option key={idx} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.role}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              bgColor="blue.500"
              textColor="white"
              onClick={handleSubmit}
              isDisabled={!isPasswordValid}
            >
              Add User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default RoleManagement;
